import { getUrl, uploadData, downloadData, list as listV6, remove as removeV6, copy as copyV6 } from 'aws-amplify/storage';

const PUBLIC_PREFIX = 'public/';

// Presigned URL ký bằng credential tạm của Cognito không sống lâu hơn credential (~1h),
// nên giá trị lớn hơn mốc này trên thực tế không có tác dụng. Cảnh báo chứ không cắt,
// để giữ đúng hành vi của v2 (v2 cũng truyền thẳng xuống signer).
const EFFECTIVE_MAX_EXPIRES_SECONDS = 3600;

export const toPath = key => {
    if (typeof key !== 'string') {
        return key;
    }
    const trimmed = key.replace(/^\/+/, '');
    return trimmed.startsWith(PUBLIC_PREFIX) ? trimmed : `${PUBLIC_PREFIX}${trimmed}`;
};

export const fromPath = path => {
    if (typeof path !== 'string') {
        return path;
    }
    return path.replace(/^\/?public\//, '');
};

const warnUnsupported = options => {
    if (options?.acl) {
        console.warn('[amplify] Storage option `acl` không còn được hỗ trợ ở Amplify v6 - đã bỏ qua');
    }
    if (typeof options?.expires === 'number' && options.expires > EFFECTIVE_MAX_EXPIRES_SECONDS) {
        console.warn(`[amplify] Storage expires=${options.expires}s: presigned URL không thể sống lâu hơn credential tạm (~${EFFECTIVE_MAX_EXPIRES_SECONDS}s)`);
    }
};

/**
 * v2 `Storage.get` trả về string URL, hoặc `{ Body }` khi `download: true`.
 * v6 tách thành hai API khác nhau và `getUrl` trả `{ url: URL }` - KHÔNG phải string.
 */
const get = async (key, options = {}) => {
    warnUnsupported(options);
    const path = toPath(key);

    if (options.download) {
        // `downloadData` không có tương đương cho `cacheControl`. Khi caller yêu cầu
        // no-cache (logService.js), đi qua presigned URL + fetch để giữ đúng ngữ nghĩa.
        if (options.cacheControl) {
            const { url } = await getUrl({ path, options: { expiresIn: options.expires } });
            const res = await fetch(url.toString(), { cache: 'no-store' });
            return { Body: await res.blob() };
        }
        const result = await downloadData({ path }).result;
        return { ...result, Body: await result.body.blob(), key: fromPath(result.path) };
    }

    const { url } = await getUrl({
        path,
        options: {
            expiresIn: options.expires,
            contentDisposition: options.contentDisposition,
            contentType: options.contentType,
            validateObjectExistence: options.validateObjectExistence
        }
    });
    return url.toString();
};

/**
 * v2 `progressCallback({ loaded, total })` → v6 `onProgress({ transferredBytes, totalBytes })`.
 * Wrapped in try/catch: for multipart uploads (>= 5MB), v6 calls onProgress synchronously within
 * an async loop (uploadPartExecutor), so if the callback throws an error, it will reject the entire upload.
 * Errors from the caller must not break the upload.
 */
export const toOnProgress = progressCallback => {
    if (typeof progressCallback !== 'function') {
        return undefined;
    }
    return ({ transferredBytes, totalBytes } = {}) => {
        try {
            progressCallback({ loaded: transferredBytes, total: totalBytes });
        } catch (err) {
            console.warn('[amplify] Storage progressCallback lỗi - đã bỏ qua', err);
        }
    };
};

/**
 * v2 trả `{ key }`. v6 `uploadData().result` trả `{ path }` và KHÔNG có `key`.
 * 6 call site đọc `result.key` (ChatInput, EditChatPost, RichTextEditor*, EmailRichtext) —
 * thiếu map này thì không crash, chỉ ra `fileUrl: undefined`, tức lỗi âm thầm.
 */
const put = async (key, data, options = {}) => {
    warnUnsupported(options);
    const path = toPath(key);

    const result = await uploadData({
        path,
        data,
        options: {
            contentType: options.contentType,
            contentDisposition: options.contentDisposition,
            cacheControl: options.cacheControl,
            metadata: options.metadata,
            onProgress: toOnProgress(options.progressCallback),
            // Opt-in only: caller passes this when large-file multipart upload needs to skip the
            // post-complete HeadObject integrity check (IAM role may lack s3:GetObject on the path).
            checksumAlgorithm: options.checksumAlgorithm
        }
    }).result;

    return { ...result, key: fromPath(result.path), path: result.path };
};

/**
 * v2 trả thẳng một array item có `key`. v6 trả `{ items }` và item có `path`.
 * `LogScreenPage/index.js:222` kiểm tra `listFile?.length === 0` và `for (const {key} of listFile)`
 * nên shim phải trả về array đã map sẵn `key`.
 */
const list = async (path, options = {}) => {
    warnUnsupported(options);
    const { items } = await listV6({
        path: toPath(path),
        options: { listAll: true }
    });
    return items.map(item => ({ ...item, key: fromPath(item.path) }));
};

const remove = async (key, options = {}) => {
    const result = await removeV6({ path: toPath(key) });
    return { ...result, key: fromPath(result.path) };
};

const copy = async (src, dest, options = {}) => {
    const result = await copyV6({
        source: { path: toPath(src?.key ?? src) },
        destination: { path: toPath(dest?.key ?? dest) }
    });
    return { ...result, key: fromPath(result.path) };
};

export default { get, put, remove, list, copy, toPath, fromPath };
